import type { ExperimentsClient } from "@databricks/sdk-experiments/v1";
import type { StatementExecutionClient } from "@databricks/sdk-statementexecution/v1";
import type { TablesClient } from "@databricks/sdk-uc-tables/v1";
import type { FilesystemEntry, FilesystemSource, JsonObject, ToolDef } from "@fabric-harness/sdk";
import type {
  DatabricksJobRunPolicy,
  DatabricksJobsClient,
  DatabricksNotebookRunPolicy,
} from "./jobs.js";
import { type DatabricksRunReceipt, databricksJobs } from "./jobs.js";
import type { DatabricksRawProtocolClient } from "./raw-protocol-client.js";
import {
  DatabricksResourceNotAllowedError,
  assertExactResourceAllowed,
  pinnedStringResourceSchema,
  resolveExactResourcePolicy,
} from "./resource-policy.js";

export type DatabricksExperimentsClient = Pick<ExperimentsClient, "logMetric" | "logParam">;
export type DatabricksTablesClient = Pick<TablesClient, "listTables" | "getTable">;
export type DatabricksSqlClient = Pick<StatementExecutionClient, "executeStatement">;

export interface DatabricksSqlToolOptions {
  warehouseId: string;
  name?: string;
  description?: string;
  waitTimeout?: string;
  disposition?: "INLINE" | "EXTERNAL_LINKS";
}

export type DatabricksSqlReadToolOptions = DatabricksSqlToolOptions;
export interface DatabricksSqlStatementInput {
  statement: string;
  catalog?: string;
  schema?: string;
}
export type DatabricksSqlExecutionPolicy =
  | {
      allowedStatements: readonly string[];
      validateStatement?: never;
      allowAnyStatement?: never;
    }
  | {
      allowedStatements?: never;
      validateStatement: (
        input: Readonly<DatabricksSqlStatementInput>,
      ) => boolean | Promise<boolean>;
      allowAnyStatement?: never;
    }
  | {
      allowedStatements?: never;
      validateStatement?: never;
      allowAnyStatement: true;
    };
export type DatabricksMlflowRunPolicy =
  | { allowedRunIds: readonly string[]; allowAnyRunId?: never }
  | { allowedRunIds?: never; allowAnyRunId: true };

const UNSAFE_SQL_KEYWORDS = new Set([
  "ADD",
  "ALTER",
  "ANALYZE",
  "CACHE",
  "CALL",
  "COMMENT",
  "COPY",
  "CREATE",
  "DELETE",
  "DROP",
  "EXECUTE",
  "GRANT",
  "INSERT",
  "INTO",
  "LIST",
  "MERGE",
  "MSCK",
  "OPTIMIZE",
  "PUT",
  "REFRESH",
  "REMOVE",
  "RESET",
  "RESTORE",
  "REVOKE",
  "SET",
  "TRANSFORM",
  "TRUNCATE",
  "UNCACHE",
  "UPDATE",
  "USE",
  "VACUUM",
]);

interface SqlToken {
  value: string;
  depth: number;
}

/**
 * A deliberately conservative SQL lexer for the model-facing read tool. It ignores quoted values,
 * identifiers, and comments while retaining statement separators and parenthesis depth. This is a
 * safety boundary, not a SQL formatter: malformed or unfamiliar statement shapes fail closed.
 */
function lexSqlReadStatement(statement: string): { tokens: SqlToken[]; semicolons: number[] } {
  const tokens: SqlToken[] = [];
  const semicolons: number[] = [];
  let depth = 0;

  for (let index = 0; index < statement.length; ) {
    const char = statement.charAt(index);
    const next = statement.charAt(index + 1);

    if (/\s/u.test(char)) {
      index += 1;
      continue;
    }
    if (char === "-" && next === "-") {
      index += 2;
      while (index < statement.length && statement[index] !== "\n") index += 1;
      continue;
    }
    if (char === "/" && next === "*") {
      const end = statement.indexOf("*/", index + 2);
      if (end < 0) throw new Error("Databricks SQL read denied: unterminated block comment.");
      index = end + 2;
      continue;
    }
    if (char === "'" || char === '"' || char === "`") {
      const quote = char;
      index += 1;
      let closed = false;
      while (index < statement.length) {
        if (statement[index] === quote) {
          if (statement[index + 1] === quote) {
            index += 2;
            continue;
          }
          index += 1;
          closed = true;
          break;
        }
        if (statement[index] === "\\" && quote === "'" && index + 1 < statement.length) {
          index += 2;
          continue;
        }
        index += 1;
      }
      if (!closed) throw new Error("Databricks SQL read denied: unterminated quoted value.");
      continue;
    }
    if (char === ";") {
      semicolons.push(index);
      index += 1;
      continue;
    }
    if (char === "(") {
      depth += 1;
      index += 1;
      continue;
    }
    if (char === ")") {
      depth -= 1;
      if (depth < 0) throw new Error("Databricks SQL read denied: unbalanced parentheses.");
      index += 1;
      continue;
    }
    if (/[A-Za-z_]/u.test(char)) {
      const start = index;
      index += 1;
      while (index < statement.length && /[A-Za-z0-9_$]/u.test(statement.charAt(index))) index += 1;
      tokens.push({ value: statement.slice(start, index).toUpperCase(), depth });
      continue;
    }
    index += 1;
  }

  if (depth !== 0) throw new Error("Databricks SQL read denied: unbalanced parentheses.");
  return { tokens, semicolons };
}

function assertDatabricksSqlReadStatement(statement: string): void {
  if (!statement.trim()) throw new Error("Databricks SQL read denied: statement cannot be empty.");
  const { tokens, semicolons } = lexSqlReadStatement(statement);
  if (tokens.length === 0)
    throw new Error("Databricks SQL read denied: expected a SELECT statement.");

  if (semicolons.length > 1)
    throw new Error("Databricks SQL read denied: multiple statements are not allowed.");
  if (semicolons.length === 1) {
    const tail = statement.slice((semicolons[0] ?? -1) + 1);
    const trailing = lexSqlReadStatement(tail);
    if (trailing.tokens.length > 0 || trailing.semicolons.length > 0)
      throw new Error("Databricks SQL read denied: multiple statements are not allowed.");
  }

  const first = tokens[0]?.value;
  const topLevel = tokens.filter((token) => token.depth === 0).map((token) => token.value);
  const mainKeyword = first === "WITH" ? topLevel.find((token) => token === "SELECT") : first;
  if (mainKeyword !== "SELECT")
    throw new Error("Databricks SQL read denied: only SELECT queries are allowed.");

  const unsafe = tokens.find((token) => UNSAFE_SQL_KEYWORDS.has(token.value));
  if (unsafe)
    throw new Error(`Databricks SQL read denied: unsafe keyword ${unsafe.value} is not allowed.`);
}

export function databricksSqlTool(
  client: DatabricksSqlClient,
  policy: DatabricksSqlExecutionPolicy,
  options: DatabricksSqlToolOptions,
): ToolDef<DatabricksSqlStatementInput, unknown> {
  const executionPolicy = resolveSqlExecutionPolicy(policy);
  return {
    name: options.name ?? "databricks_sql",
    description: options.description ?? "Run a SQL statement on a Databricks SQL Warehouse.",
    inputSchema: {
      type: "object",
      additionalProperties: false,
      properties: {
        statement:
          executionPolicy.kind === "allowlist"
            ? pinnedStringResourceSchema(executionPolicy.statements)
            : { type: "string" },
        catalog: { type: "string" },
        schema: { type: "string" },
      },
      required: ["statement"],
    },
    metadata: {
      effect: "execute",
      provider: "databricks",
      service: "sql",
      governance: {
        resources: [{ kind: "sql-warehouse", value: options.warehouseId }],
      },
    },
    async execute(input) {
      await assertSqlExecutionAllowed(input, executionPolicy);
      return await client.executeStatement({
        warehouseId: options.warehouseId,
        statement: input.statement,
        waitTimeout: options.waitTimeout ?? "30s",
        disposition: options.disposition ?? "INLINE",
        ...(input.catalog ? { catalog: input.catalog } : {}),
        ...(input.schema ? { schema: input.schema } : {}),
      });
    },
  };
}

type ResolvedSqlExecutionPolicy =
  | { kind: "allow-any" }
  | { kind: "allowlist"; statements: readonly string[] }
  | {
      kind: "validator";
      validate: (input: Readonly<DatabricksSqlStatementInput>) => boolean | Promise<boolean>;
    };

function resolveSqlExecutionPolicy(
  policy: DatabricksSqlExecutionPolicy,
): ResolvedSqlExecutionPolicy {
  if (!policy || typeof policy !== "object" || Array.isArray(policy))
    throw new TypeError("Databricks SQL execution policy must be an object.");
  if ("validateStatement" in policy && typeof policy.validateStatement === "function")
    return { kind: "validator", validate: policy.validateStatement };
  const statements = resolveExactResourcePolicy(
    policy,
    "allowedStatements",
    "allowAnyStatement",
    "SQL statement",
  );
  return statements ? { kind: "allowlist", statements } : { kind: "allow-any" };
}

async function assertSqlExecutionAllowed(
  input: DatabricksSqlStatementInput,
  policy: ResolvedSqlExecutionPolicy,
): Promise<void> {
  if (policy.kind === "allow-any") return;
  if (policy.kind === "allowlist") {
    assertExactResourceAllowed("SQL statement", input.statement, policy.statements);
    return;
  }
  if ((await policy.validate(input)) !== true)
    throw new DatabricksResourceNotAllowedError("SQL statement");
}

/**
 * Run one read-only query on a Databricks SQL Warehouse. The tool accepts SELECT (including
 * SELECT-ending CTEs), rejects mutation/administration keywords, and denies multiple statements
 * before resolving credentials or calling Statement Execution.
 */
export function databricksSqlReadTool(
  client: DatabricksSqlClient,
  options: DatabricksSqlReadToolOptions,
): ToolDef<{ statement: string; catalog?: string; schema?: string }, unknown> {
  return {
    name: options.name ?? "sql_read",
    description:
      options.description ??
      "Run one SELECT-only query on a Databricks SQL Warehouse. Mutations and multiple statements are denied.",
    inputSchema: {
      type: "object",
      additionalProperties: false,
      properties: {
        statement: { type: "string" },
        catalog: { type: "string" },
        schema: { type: "string" },
      },
      required: ["statement"],
    },
    metadata: {
      effect: "read",
      provider: "databricks",
      service: "sql",
      governance: {
        resources: [{ kind: "catalog", path: "/catalog", catalogQualified: true, optional: true }],
      },
    },
    async execute(input) {
      assertDatabricksSqlReadStatement(input.statement);
      return await client.executeStatement({
        warehouseId: options.warehouseId,
        statement: input.statement,
        waitTimeout: options.waitTimeout ?? "30s",
        disposition: options.disposition ?? "INLINE",
        ...(input.catalog ? { catalog: input.catalog } : {}),
        ...(input.schema ? { schema: input.schema } : {}),
      });
    },
  };
}

/**
 * Trigger an existing Databricks job. `runPolicy` is required: the run target is model-supplied, so
 * the bound is part of constructing the tool rather than an option a caller can forget. The pinned
 * schema is a model-facing hint; `databricksJobs()` re-checks the id before calling the Jobs API.
 */
export function databricksRunJobTool(
  client: DatabricksJobsClient,
  runPolicy: DatabricksJobRunPolicy,
  options: { name?: string; description?: string } = {},
): ToolDef<
  {
    jobId: number;
    idempotencyToken?: string;
    notebookParams?: JsonObject;
    pythonParams?: string[];
    jarParams?: string[];
  },
  DatabricksRunReceipt
> {
  const jobs = databricksJobs(client, { runPolicy });
  return {
    name: options.name ?? "databricks_run_job",
    description: options.description ?? "Trigger an existing Databricks job by job id.",
    inputSchema: {
      type: "object",
      additionalProperties: false,
      properties: {
        jobId: pinnedJobIdSchema(runPolicy),
        idempotencyToken: { type: "string" },
        notebookParams: { type: "object", additionalProperties: true },
        pythonParams: { type: "array", items: { type: "string" } },
        jarParams: { type: "array", items: { type: "string" } },
      },
      required: ["jobId"],
    },
    metadata: {
      effect: "execute",
      provider: "databricks",
      service: "jobs",
      governance: { resources: [{ kind: "job", path: "/jobId" }] },
    },
    execute: (input, context) =>
      jobs.runJob({
        ...input,
        ...(input.idempotencyToken
          ? {}
          : context?.toolCallId
            ? { idempotencyToken: context.toolCallId }
            : {}),
      }),
  };
}

function pinnedJobIdSchema(policy: DatabricksJobRunPolicy): Record<string, unknown> {
  const ids = policy.allowAnyJobId === true ? [] : (policy.allowedJobIds ?? []);
  if (ids.length === 1) return { type: "number", const: ids[0] };
  if (ids.length > 1) return { type: "number", enum: ids };
  return { type: "number" };
}

/**
 * Submit a one-off Databricks notebook run. `notebookPolicy` is required for the same reason the
 * run-job tool requires one, and more strongly: an unbounded notebook path executes arbitrary
 * workspace code as the calling principal.
 */
export function databricksNotebookTool(
  client: DatabricksJobsClient,
  notebookPolicy: DatabricksNotebookRunPolicy,
  options: { name?: string; description?: string; existingClusterId?: string } = {},
): ToolDef<
  {
    notebookPath: string;
    idempotencyToken?: string;
    baseParameters?: JsonObject;
    runName?: string;
  },
  DatabricksRunReceipt
> {
  const jobs = databricksJobs(client, { notebookPolicy });
  return {
    name: options.name ?? "databricks_run_notebook",
    description: options.description ?? "Submit a one-off Databricks notebook run.",
    inputSchema: {
      type: "object",
      additionalProperties: false,
      properties: {
        notebookPath: pinnedNotebookPathSchema(notebookPolicy),
        idempotencyToken: { type: "string" },
        baseParameters: { type: "object", additionalProperties: true },
        runName: { type: "string" },
      },
      required: ["notebookPath"],
    },
    metadata: {
      effect: "execute",
      provider: "databricks",
      service: "jobs",
      governance: { resources: [{ kind: "notebook", path: "/notebookPath" }] },
    },
    execute: (input, context) =>
      jobs.submitNotebook({
        ...input,
        ...(input.idempotencyToken
          ? {}
          : context?.toolCallId
            ? { idempotencyToken: context.toolCallId }
            : {}),
        ...(options.existingClusterId ? { existingClusterId: options.existingClusterId } : {}),
      }),
  };
}

/** A prefix set is not expressible as an enum, so it stays unpinned and relies on the runtime check. */
function pinnedNotebookPathSchema(policy: DatabricksNotebookRunPolicy): Record<string, unknown> {
  if (policy.allowAnyNotebookPath === true) return { type: "string" };
  const paths = policy.allowedNotebookPaths ?? [];
  if (paths.length > 0 && (policy.allowedNotebookPathPrefixes ?? []).length === 0)
    return { type: "string", enum: paths };
  return { type: "string" };
}

export function databricksMlflowLogMetricTool(
  client: DatabricksExperimentsClient,
  runPolicy: DatabricksMlflowRunPolicy,
  options: { name?: string; description?: string } = {},
): ToolDef<
  { runId: string; key: string; value: number; step?: number; timestamp?: number },
  unknown
> {
  const allowedRunIds = resolveExactResourcePolicy(
    runPolicy,
    "allowedRunIds",
    "allowAnyRunId",
    "MLflow run",
  );
  return {
    name: options.name ?? "databricks_mlflow_log_metric",
    description: options.description ?? "Log a metric to an MLflow run hosted in Databricks.",
    inputSchema: {
      type: "object",
      additionalProperties: false,
      properties: {
        runId: pinnedStringResourceSchema(allowedRunIds),
        key: { type: "string" },
        value: { type: "number" },
        step: { type: "number" },
        timestamp: { type: "number" },
      },
      required: ["runId", "key", "value"],
    },
    metadata: {
      effect: "write",
      provider: "databricks",
      service: "mlflow",
      governance: { resources: [{ kind: "mlflow-run", path: "/runId" }] },
    },
    async execute(input) {
      assertExactResourceAllowed("MLflow run", input.runId, allowedRunIds);
      return await retryIdempotent(() =>
        client.logMetric({
          runId: input.runId,
          key: input.key,
          value: input.value,
          timestamp: BigInt(input.timestamp ?? Date.now()),
          ...(input.step !== undefined ? { step: BigInt(input.step) } : {}),
        }),
      );
    },
  };
}

export function databricksMlflowLogParamTool(
  client: DatabricksExperimentsClient,
  runPolicy: DatabricksMlflowRunPolicy,
  options: { name?: string; description?: string } = {},
): ToolDef<{ runId: string; key: string; value: string }, unknown> {
  const allowedRunIds = resolveExactResourcePolicy(
    runPolicy,
    "allowedRunIds",
    "allowAnyRunId",
    "MLflow run",
  );
  return {
    name: options.name ?? "databricks_mlflow_log_param",
    description: options.description ?? "Log a parameter to an MLflow run hosted in Databricks.",
    inputSchema: {
      type: "object",
      additionalProperties: false,
      properties: {
        runId: pinnedStringResourceSchema(allowedRunIds),
        key: { type: "string" },
        value: { type: "string" },
      },
      required: ["runId", "key", "value"],
    },
    metadata: {
      effect: "write",
      provider: "databricks",
      service: "mlflow",
      governance: { resources: [{ kind: "mlflow-run", path: "/runId" }] },
    },
    async execute(input) {
      assertExactResourceAllowed("MLflow run", input.runId, allowedRunIds);
      return await retryIdempotent(() =>
        client.logParam({ runId: input.runId, key: input.key, value: input.value }),
      );
    },
  };
}

async function retryIdempotent<T>(call: () => Promise<T>, attempts = 3): Promise<T> {
  let last: unknown;
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      return await call();
    } catch (error) {
      last = error;
      const status = (error as { httpStatusCode?: unknown }).httpStatusCode;
      if (status !== 429 && (typeof status !== "number" || status < 500 || status >= 600))
        throw error;
      if (attempt + 1 < attempts)
        await new Promise((resolve) => setTimeout(resolve, 2 ** attempt * 25));
    }
  }
  throw last;
}

export function unityCatalogTablesTool(
  client: DatabricksTablesClient,
  options: { name?: string; description?: string } = {},
): ToolDef<{ catalog: string; schema: string }, unknown> {
  return {
    name: options.name ?? "databricks_unity_catalog_tables",
    description: options.description ?? "List Unity Catalog tables in a catalog and schema.",
    inputSchema: {
      type: "object",
      additionalProperties: false,
      properties: { catalog: { type: "string" }, schema: { type: "string" } },
      required: ["catalog", "schema"],
    },
    metadata: { effect: "read", provider: "databricks", service: "unity-catalog" },
    execute: (input) => client.listTables({ catalogName: input.catalog, schemaName: input.schema }),
  };
}

export function databricksTableInfoTool(
  client: DatabricksTablesClient,
  options: { name?: string; description?: string } = {},
): ToolDef<{ fullName: string }, unknown> {
  return {
    name: options.name ?? "databricks_table_info",
    description:
      options.description ??
      "Describe a Unity Catalog table (columns, types, comment) by its full name (catalog.schema.table).",
    inputSchema: {
      type: "object",
      additionalProperties: false,
      properties: { fullName: { type: "string" } },
      required: ["fullName"],
    },
    metadata: { effect: "read", provider: "databricks", service: "unity-catalog" },
    execute: (input) => client.getTable({ fullNameArg: input.fullName }),
  };
}

export interface DatabricksWorkspaceSourceOptions {
  name?: string;
  include?: (path: string) => boolean;
  recursive?: boolean;
  format?: "SOURCE" | "HTML" | "JUPYTER" | "DBC";
}

export function databricksWorkspaceSource(
  client: DatabricksRawProtocolClient,
  rootPath: string,
  options: DatabricksWorkspaceSourceOptions = {},
): FilesystemSource {
  const include = options.include ?? (() => true);
  return {
    name: options.name ?? `databricks-workspace:${rootPath}`,
    async *entries(): AsyncIterable<FilesystemEntry> {
      yield* walkWorkspace(
        client,
        rootPath,
        rootPath,
        include,
        options.recursive ?? true,
        options.format ?? "SOURCE",
      );
    },
  };
}

async function* walkWorkspace(
  client: DatabricksRawProtocolClient,
  root: string,
  current: string,
  include: (path: string) => boolean,
  recursive: boolean,
  format: string,
): AsyncIterable<FilesystemEntry> {
  const listing = await client.get<{ objects?: Array<{ path: string; object_type?: string }> }>(
    "/api/2.0/workspace/list",
    { path: current },
  );
  for (const object of listing.objects ?? []) {
    if (object.object_type === "DIRECTORY") {
      if (recursive) yield* walkWorkspace(client, root, object.path, include, recursive, format);
      continue;
    }
    const relative = object.path.slice(root.length).replace(/^\/+/, "");
    if (!relative || !include(relative)) continue;
    const exported = await client.get<{ content?: string }>("/api/2.0/workspace/export", {
      path: object.path,
      format,
    });
    yield {
      path: relative,
      content: exported.content ? base64ToBytes(exported.content) : new Uint8Array(),
    };
  }
}

function base64ToBytes(value: string): Uint8Array {
  if (typeof Buffer !== "undefined") return new Uint8Array(Buffer.from(value, "base64"));
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}
